import { Close } from '@mui/icons-material';
import { Drawer, IconButton, Grid, FormControl, InputLabel, Select, MenuItem, Button, TextField } from '@mui/material';
import { Box } from '@mui/system';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { UserRole } from 'Application/models';
import { useAgencyStore } from 'Application/store';
import { getUserRoleNameFromStorage } from 'Application/utils';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';
import { toast } from 'sonner';

interface LocalTravelFiltersProps {
    isDrawerOpen: boolean;
    setOpenDrawer: (openDrawer: boolean) => void;
    agencyName: string;
    pageNumber: number;
    pageSize: number;
}

export const LocalTravelFilters: FC<LocalTravelFiltersProps> = ({ isDrawerOpen, setOpenDrawer, agencyName, pageNumber, pageSize }) => {
    // States for filter values (Temporary storage until applied)
    const [tempSvNumber, setTempSvNumber] = useState<string>('');
    const [tempBranch, setTempBranch] = useState<string>('');
    const [tempStartDate, setTempStartDate] = useState<string | null>(null);
    const [tempEndDate, setTempEndDate] = useState<string | null>(null);

    const {
        isLoading,
        error: getAgencyLocalDataError,
        getFilteredAgencyLocalTravelData,
        getAgencyLocalTravelData,
        agencyBranches
    } = useAgencyStore();

    const userRole = getUserRoleNameFromStorage();

    // Applied filter states (Only update when "Apply Filters" is clicked)
    const [filters, setFilters] = useState({
        svNumber: '',
        branch: '',
        startDate: '',
        endDate: ''
    });

    const [hasFilters, setHasFilters] = useState<boolean>(false);
    const [isFiltersCleared, setIsFiltersCleared] = useState<boolean>(false);

    const clearFilters = () => {
        setTempSvNumber('');
        setTempBranch('');
        setTempStartDate(null);
        setTempEndDate(null);
        setIsFiltersCleared(true);

        setFilters((prevFilters) => {
            return {
                ...prevFilters,
                svNumber: '',
                branch: '',
                startDate: '',
                endDate: ''
            };
        });
    };

    const applyFilters = () => {
        setHasFilters(true);
        setIsFiltersCleared(false);
        setFilters((prevFilters) => {
            return {
                ...prevFilters,
                svNumber: tempSvNumber,
                branch: tempBranch,
                startDate: tempStartDate ?? '',
                endDate: tempEndDate ?? ''
            };
        });
    };

    const fetchFilteredAgencyPerksData = async () => {
        const { branch, svNumber, startDate, endDate } = filters;

        try {
            await getFilteredAgencyLocalTravelData(
                agencyName,
                branch === '' ? undefined : branch,
                svNumber === '' ? undefined : svNumber,
                startDate === '' ? undefined : dayjs(startDate).format('YYYY-MM-DD'),
                endDate === '' ? undefined : dayjs(endDate).format('YYYY-MM-DD')
            );
            toast.success('Agency local travel data found successfully');
        } catch (error) {
            toast.error(getAgencyLocalDataError);
            throw error;
        } finally {
            setOpenDrawer(false);
        }
    };

    const fetchUnfilteredAgencyPerksData = async () => {
        try {
            await getAgencyLocalTravelData(agencyName, pageNumber, pageSize);
            toast.success('Agency local travel data found successfully');
        } catch (error) {
            toast.error(getAgencyLocalDataError);
            throw error;
        } finally {
            setOpenDrawer(false);
        }
    };

    useEffect(() => {
        if (hasFilters && !isFiltersCleared) {
            fetchFilteredAgencyPerksData();
        }
    }, [hasFilters, isFiltersCleared, filters.svNumber, filters.branch, filters.startDate, filters.endDate]);

    useEffect(() => {
        if (isFiltersCleared) {
            fetchUnfilteredAgencyPerksData();
        }
    }, [isFiltersCleared]);

    return (
        <Drawer anchor="right" open={isDrawerOpen} onClose={() => setOpenDrawer(false)}>
            <Box width={350} p={3} role="presentation">
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <h2>Filters</h2>
                    <IconButton onClick={() => setOpenDrawer(false)}>
                        <Close />
                    </IconButton>
                </Box>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Grid container spacing={2} mt={2}>
                        {/* SV number*/}
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label="SV number"
                                    value={tempSvNumber}
                                    onChange={(event) => setTempSvNumber(event.target.value)}
                                />
                            </FormControl>
                        </Grid>

                        {/* Agency branches */}
                        {userRole !== UserRole.ROLE_AGENT && userRole !== UserRole.ROLE_BRANCH_MANAGER && (
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="agency-branch-label">Branch</InputLabel>
                                    <Select
                                        labelId="agency-branch-label"
                                        value={tempBranch}
                                        onChange={(e) => setTempBranch(e.target.value)}
                                        label="Agency Name"
                                    >
                                        {agencyBranches.length === 0 ? (
                                            <MenuItem disabled>Loading...</MenuItem>
                                        ) : (
                                            agencyBranches?.map((branch) => {
                                                const branchIndex = agencyBranches.indexOf(branch);
                                                const branchName = branch.split(' ')[0];

                                                return (
                                                    <MenuItem key={branchIndex} value={branchName}>
                                                        {branchName}
                                                    </MenuItem>
                                                );
                                            })
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        {/* Date Pickers */}
                        <Grid item xs={6}>
                            <DatePicker label="Start Date" value={tempStartDate} onChange={(newValue) => setTempStartDate(newValue)} />
                        </Grid>
                        <Grid item xs={6}>
                            <DatePicker label="End Date" value={tempEndDate} onChange={(newValue) => setTempEndDate(newValue)} />
                        </Grid>
                    </Grid>
                </LocalizationProvider>

                {/* Buttons */}
                <Box mt={3} display="flex" justifyContent="space-between">
                    <Button variant="outlined" color="secondary" onClick={clearFilters} disabled={isLoading}>
                        Clear Filters
                    </Button>

                    <Button variant="contained" onClick={applyFilters} disabled={isLoading}>
                        Apply Filters
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
};
